import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import CDTCodeLookup from './CDTCodeLookup';
import Input from '../ui/Input';
import Select from '../ui/Select';

const DentalProcedurePanel = ({ procedures = [], onAdd, onRemove, onUpdate }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newProcedure, setNewProcedure] = useState({
    cdtCode: null,
    status: 'Proposed',
    notes: '',
    scheduledDate: new Date()?.toISOString()?.split('T')?.[0],
    tooth: ''
  });

  const statusOptions = [
    { value: 'Proposed', label: 'Proposed' },
    { value: 'Scheduled', label: 'Scheduled' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Completed', label: 'Completed' }
  ];

  const handleAddProcedure = () => {
    if (newProcedure?.cdtCode) {
      onAdd?.({
        id: Date?.now(),
        code: newProcedure?.cdtCode?.code,
        description: newProcedure?.cdtCode?.description,
        category: newProcedure?.cdtCode?.category,
        status: newProcedure?.status,
        notes: newProcedure?.notes,
        tooth: newProcedure?.tooth,
        scheduledDate: newProcedure?.scheduledDate,
        addedDate: new Date()?.toISOString()
      });
      setNewProcedure({
        cdtCode: null,
        status: 'Proposed',
        notes: '',
        scheduledDate: new Date()?.toISOString()?.split('T')?.[0],
        tooth: ''
      });
      setIsAdding(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-success/10 text-success border-success';
      case 'In Progress':
        return 'bg-primary/10 text-primary border-primary';
      case 'Scheduled':
        return 'bg-warning/10 text-warning border-warning';
      case 'Proposed':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">Dental Procedures</h2>
            <p className="text-sm text-muted-foreground mt-1">Manage patient procedures with CDT codes</p>
          </div>
          {!isAdding && (
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setIsAdding(true)}
            >
              Add Procedure
            </Button>
          )}
        </div>
      </div>

      {isAdding && (
        <div className="p-4 md:p-6 border-b border-border bg-muted/30">
          <h3 className="text-base font-semibold text-foreground mb-4">Add New Procedure</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                CDT Code <span className="text-destructive">*</span>
              </label>
              <CDTCodeLookup
                onSelect={(cdt) => setNewProcedure({ ...newProcedure, cdtCode: cdt })}
                selectedCodes={newProcedure?.cdtCode}
              />
              {newProcedure?.cdtCode && (
                <div className="mt-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {newProcedure?.cdtCode?.code} - {newProcedure?.cdtCode?.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Category: {newProcedure?.cdtCode?.category}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                type="text"
                label="Tooth/Area"
                placeholder="e.g., #14, Upper Right"
                value={newProcedure?.tooth}
                onChange={(e) => setNewProcedure({ ...newProcedure, tooth: e?.target?.value })}
              />
              <Select
                label="Status"
                options={statusOptions}
                value={newProcedure?.status}
                onChange={(value) => setNewProcedure({ ...newProcedure, status: value })}
              />
              <Input
                type="date"
                label="Scheduled Date"
                value={newProcedure?.scheduledDate}
                onChange={(e) => setNewProcedure({ ...newProcedure, scheduledDate: e?.target?.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Treatment Notes</label>
              <textarea
                value={newProcedure?.notes}
                onChange={(e) => setNewProcedure({ ...newProcedure, notes: e?.target?.value })}
                placeholder="Add treatment notes, procedure details, or additional context..."
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleAddProcedure}
                disabled={!newProcedure?.cdtCode}
              >
                Save Procedure
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsAdding(false);
                  setNewProcedure({
                    cdtCode: null,
                    status: 'Proposed',
                    notes: '',
                    scheduledDate: new Date()?.toISOString()?.split('T')?.[0],
                    tooth: ''
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
        {procedures?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
              <Icon name="Activity" size={32} className="text-muted-foreground" />
            </div>
            <p className="text-base font-medium text-foreground mb-2">No Procedures</p>
            <p className="text-sm text-muted-foreground text-center">Add patient procedures with CDT codes</p>
          </div>
        ) : (
          procedures?.map((procedure) => (
            <div key={procedure?.id} className="p-4 md:p-6 hover:bg-muted/30 transition-smooth">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0">
                      <Icon name="Activity" size={20} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-semibold text-foreground">{procedure?.code}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(procedure?.status)}`}>
                          {procedure?.status}
                        </span>
                      </div>
                      <p className="text-sm text-foreground font-medium mb-1">{procedure?.description}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="Tag" size={12} />
                          {procedure?.category}
                        </span>
                        {procedure?.tooth && (
                          <span className="flex items-center gap-1">
                            <Icon name="MapPin" size={12} />
                            {procedure?.tooth}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Icon name="Calendar" size={12} />
                          {new Date(procedure?.scheduledDate)?.toLocaleDateString()}
                        </span>
                      </div>
                      {procedure?.notes && (
                        <p className="text-sm text-muted-foreground mt-2 p-2 bg-muted/50 rounded">
                          {procedure?.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemove?.(procedure?.id)}
                  className="flex-shrink-0"
                >
                  <Icon name="Trash2" size={16} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DentalProcedurePanel;